import { Brand, MosyUser } from './types';
import { Avatar } from '../components/ui/Avatar';
import { userDisplayName } from './users';

export const brandInitials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

/** Pick readable text + an optional border for a given bg color, so light
 *  brand colors (white, very pale tints) still render legibly. */
export const readableOn = (hex: string): { color: string; border?: string } => {
  const m = /^#([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return { color: '#FAFAF7' };
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  // Standard relative luminance approximation
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (lum > 0.82) {
    return { color: 'var(--ink)', border: '1px solid var(--border-strong)' };
  }
  return { color: '#FAFAF7' };
};

export const BrandAvatar = ({
  brand,
  size = 24,
}: {
  brand: Pick<Brand, 'name' | 'color' | 'logo'>;
  size?: number;
}) => {
  const { color, border } = readableOn(brand.color);
  return (
    <Avatar
      initials={brandInitials(brand.name)}
      bg={brand.color}
      color={color}
      border={border}
      imageUrl={brand.logo ?? undefined}
      size={size}
      shape="rounded"
      title={brand.name}
      imageObjectFit="contain"
    />
  );
};

export const BrandStack = ({
  brands,
  max = 4,
  size = 22,
}: {
  brands: Pick<Brand, 'name' | 'color' | 'logo'>[];
  max?: number;
  size?: number;
}) => {
  const shown = brands.slice(0, max);
  const overflow = brands.length - shown.length;
  return (
    <div className="avatar-stack">
      {shown.map((b, i) => (
        <BrandAvatar key={b.name + i} brand={b} size={size} />
      ))}
      {overflow > 0 && (
        <div
          className="avatar"
          style={{ width: size, height: size, fontSize: size * 0.42 }}
          title={`+${overflow}`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
};

export const userInitials = (u: Pick<MosyUser, 'first_name' | 'last_name' | 'username' | 'initials'>): string => {
  if (u.initials && u.initials.length > 0) return u.initials.slice(0, 2).toUpperCase();
  const f = (u.first_name || u.username || '').charAt(0);
  const l = (u.last_name || '').charAt(0);
  return `${f}${l}`.toUpperCase() || '?';
};

export const OwnerAvatar = ({
  user,
  size = 22,
  title,
}: {
  user: Pick<MosyUser, 'first_name' | 'last_name' | 'username' | 'color' | 'avatar' | 'initials'> | null | undefined;
  size?: number;
  title?: string;
}) => {
  if (!user) {
    return (
      <div
        className="avatar"
        style={{ width: size, height: size, fontSize: size * 0.44, color: 'var(--ink-4)' }}
        title={title || 'Unassigned'}
      >
        —
      </div>
    );
  }
  return (
    <Avatar
      initials={userInitials(user)}
      bg={user.color || 'var(--bg-active)'}
      color="#FAFAF7"
      imageUrl={user.avatar ?? undefined}
      size={size}
      title={title || userDisplayName(user)}
    />
  );
};

export const AssigneeStack = ({
  users,
  max = 3,
  size = 22,
  onClick,
}: {
  users: Pick<MosyUser, 'first_name' | 'last_name' | 'username' | 'color' | 'avatar' | 'initials'>[];
  max?: number;
  size?: number;
  onClick?: () => void;
}) => {
  if (users.length === 0) {
    return (
      <button
        type="button"
        className="avatar"
        style={{
          width: size,
          height: size,
          fontSize: size * 0.5,
          background: 'transparent',
          border: '1.5px dashed var(--border-strong)',
          color: 'var(--ink-5)',
          cursor: onClick ? 'pointer' : 'default',
          padding: 0,
        }}
        onClick={onClick}
        aria-label="Assign"
      >
        +
      </button>
    );
  }
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;
  return (
    <div className="avatar-stack" onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
      {shown.map((u, i) => (
        <OwnerAvatar key={(u.username || '') + i} user={u} size={size} />
      ))}
      {overflow > 0 && (
        <div
          className="avatar"
          style={{ width: size, height: size, fontSize: size * 0.42 }}
          title={`+${overflow}`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
};
